import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { UserLevel } from '@/types/auth.types';
import { useAuth } from './AuthContext';

interface NavigationContextType {
  currentPage: string;
  patientId?: number;
  navigate: (page: string, patientId?: number) => void;
  canNavigateTo: (page: string) => boolean;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const useNavigation = (): NavigationContextType => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

interface NavigationProviderProps {
  children: React.ReactNode;
}

const pagePermissions: Record<string, UserLevel[]> = {
  // Dashboard pages - each user can access their own dashboard
  'maternity-dashboard': [UserLevel.MATERNITY, UserLevel.HOSPITAL, UserLevel.ADMINISTRATOR],
  'hospital-dashboard': [UserLevel.HOSPITAL, UserLevel.ADMINISTRATOR],
  'admin-dashboard': [UserLevel.ADMINISTRATOR],

  // Section-specific dashboards for hospital users
  'prematuri-dashboard': [UserLevel.HOSPITAL, UserLevel.ADMINISTRATOR],
  'chd-dashboard': [UserLevel.HOSPITAL, UserLevel.ADMINISTRATOR],

  // Maternity pages
  'new-case': [UserLevel.MATERNITY, UserLevel.HOSPITAL, UserLevel.ADMINISTRATOR],
  'maternal-data': [UserLevel.MATERNITY, UserLevel.HOSPITAL, UserLevel.ADMINISTRATOR],
  'patients': [UserLevel.MATERNITY, UserLevel.HOSPITAL, UserLevel.ADMINISTRATOR],
  'patient-detail': [UserLevel.MATERNITY, UserLevel.HOSPITAL, UserLevel.ADMINISTRATOR],

  // Hospital pages
  'procedures': [UserLevel.HOSPITAL, UserLevel.ADMINISTRATOR],
  'diagnoses': [UserLevel.HOSPITAL, UserLevel.ADMINISTRATOR],
  'clinical-reports': [UserLevel.HOSPITAL, UserLevel.ADMINISTRATOR],
  'analytics': [UserLevel.HOSPITAL, UserLevel.ADMINISTRATOR],
  'emergencies': [UserLevel.HOSPITAL, UserLevel.ADMINISTRATOR],
  'premature-patients': [UserLevel.HOSPITAL, UserLevel.ADMINISTRATOR],
  'von-forms': [UserLevel.HOSPITAL, UserLevel.ADMINISTRATOR],

  // Administrator pages
  'users': [UserLevel.ADMINISTRATOR],
  'hospitals': [UserLevel.ADMINISTRATOR],
  'eurocat-reports': [UserLevel.ADMINISTRATOR],
  'system-analytics': [UserLevel.ADMINISTRATOR],
  'audit': [UserLevel.ADMINISTRATOR],
  'backup': [UserLevel.ADMINISTRATOR],
  'settings': [UserLevel.ADMINISTRATOR],
  'alerts': [UserLevel.ADMINISTRATOR],

  // Common pages
  'dashboard': [UserLevel.MATERNITY, UserLevel.HOSPITAL, UserLevel.ADMINISTRATOR],
  'profile': [UserLevel.MATERNITY, UserLevel.HOSPITAL, UserLevel.ADMINISTRATOR],
  'reports': [UserLevel.MATERNITY, UserLevel.HOSPITAL, UserLevel.ADMINISTRATOR],
};

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children }) => {
  const { utilizator, isAuthenticated } = useAuth();
  const [currentPage, setCurrentPage] = useState<string>('dashboard');
  const [patientId, setPatientId] = useState<number | undefined>(undefined);

  // Use ref to maintain current utilizator value for event listener
  const utilizatorRef = useRef(utilizator);

  // Update ref when utilizator changes
  useEffect(() => {
    utilizatorRef.current = utilizator;
  }, [utilizator]);

  // Set initial page based on user level when authenticated
  useEffect(() => {
    if (isAuthenticated && utilizator) {
      const userLevel = utilizator.user_level_id || utilizator.user_level?.id || 0;
      const defaultPage = getDefaultPageForUserLevel(userLevel);
      setCurrentPage(defaultPage);
    }
  }, [isAuthenticated, utilizator]);

  // Listen for navigation events
  useEffect(() => {
    const handleNavigate = (event: CustomEvent<{ page: string; patientId?: number }>) => {
      const { page, patientId: eventPatientId } = event.detail;

      // Special handling for new-case - dispatch modal event instead of navigation
      if (page === 'new-case') {
        const modalEvent = new CustomEvent('open-new-case-modal');
        window.dispatchEvent(modalEvent);
        return;
      }

      // Check permissions using ref to get current utilizator value
      if (!utilizatorRef.current) {
        return;
      }

      const allowedLevels = pagePermissions[page];
      if (!allowedLevels) {
        // If page not in permissions, allow navigation (for dashboards)
        setCurrentPage(page);
        setPatientId(eventPatientId);
        return;
      }

      // Get numeric user level from current ref value
      const userLevel = utilizatorRef.current.user_level_id || utilizatorRef.current.user_level?.id || 0;

      if (allowedLevels.includes(userLevel)) {
        setCurrentPage(page);
        setPatientId(eventPatientId);
      }
    };

    window.addEventListener('navigate' as any, handleNavigate);
    return () => {
      window.removeEventListener('navigate' as any, handleNavigate);
    };
    // Empty dependency array: listener stays stable, but accesses current utilizator via ref
  }, []);

  const getDefaultPageForUserLevel = (userLevel: number): string => {
    switch (userLevel) {
      case UserLevel.MATERNITY:
        return 'maternity-dashboard';
      case UserLevel.HOSPITAL: {
        // Route straight to the assigned section's dashboard instead of the
        // generic hospital-dashboard, which mixes MCC and prematuri stats
        // together regardless of which registry the user belongs to.
        const storedSection = sessionStorage.getItem('activeSection');
        const section = storedSection === 'prematuri' || storedSection === 'chd'
          ? storedSection
          : (utilizatorRef.current?.assigned_section === 'prematuri' ? 'prematuri' : 'chd');
        return section === 'prematuri' ? 'prematuri-dashboard' : 'chd-dashboard';
      }
      case UserLevel.ADMINISTRATOR:
        return 'admin-dashboard';
      default:
        return 'dashboard';
    }
  };

  const canNavigateTo = (page: string): boolean => {
    if (!utilizator) return false;
    
    const allowedLevels = pagePermissions[page];
    if (!allowedLevels) return false;
    
    const userLevel = utilizator.user_level?.id || utilizator.user_level_id || 0;
    return allowedLevels.includes(userLevel);
  };

  const navigate = (page: string, patientId?: number) => {
    if (canNavigateTo(page)) {
      setCurrentPage(page);
      setPatientId(patientId);
      // Store in localStorage for persistence
      localStorage.setItem('lastPage', page);
    } else {
      console.warn(`User does not have permission to navigate to ${page}`);
    }
  };

  const contextValue: NavigationContextType = {
    currentPage,
    patientId,
    navigate,
    canNavigateTo,
  };

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
};

export default NavigationContext;